/*
 * COPYRIGHT (c) 2023
 * HONEYWELL INC.,
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import React, { useState, useEffect } from 'react';
import SummaryTable, {
  IUtilitySummaryProps
} from '../../../shared/UtilityBillBreakdown/SummaryTable/SummaryTable';
import { useTranslation } from '@scuf/localization';
import descriptors from '../../../../locales/en-US/descriptors';
import { isObjectOrArrayAndEmpty } from '../../../shared/Utils/Utils';
import ConnectionError from '../../../shared/ErrorComponents/ConnectionError';
import withLoadingStatus from '../../../shared/StatusWrappers/LoadingStatus';
import withErrorStatus from '../../../shared/StatusWrappers/ErrorStatus';
import {
  IDetailComponentProps,
  IUtilityBillBreakdownAPIResponse
} from '../../../DashboardKPIWidgets/IEnergyManagementProps';
import { getUtilityTitle } from '../UtilityBillViewDetailsUtils';
import {
  createDynamicColummsFromExisting,
  processRowData,
  skipIfAllZero,
  eliminateNoValueDynamicColumns
} from '../../../shared/UtilityBillBreakdown/UtilityBreakdownUtils';

interface IUtilitySummaryTable extends IUtilitySummaryProps {
  onRefresh: () => Promise<void>;
  isMonthly?: boolean;
}

const UtilitySummaryTableContent = (props: IUtilitySummaryTable) => {
  const d = descriptors.energyManagement;
  const [t] = useTranslation();
  const {
    rowData,
    columnDetails,
    fileName,
    onRefresh,
    costCurrency,
    isMonthly
  } = props;
  if (isObjectOrArrayAndEmpty(rowData)) {
    return (
      <div className="errorDisplay">
        <ConnectionError
          title={t(d.CONNECTIONERRORTITLE)}
          message={t(d.CONNECTIONERROR)}
          onRefresh={onRefresh}
        />
      </div>
    );
  }
  return (
    <div className="utility-bill-summary-component-wrapper">
      <SummaryTable
        fileName={fileName}
        rowData={rowData}
        columnDetails={columnDetails}
        costCurrency={costCurrency}
        utilityType={props.utilityType}
        isMonthly={isMonthly}
      />
    </div>
  );
};

const SummaryTableWithLoader = withLoadingStatus(
  withErrorStatus(UtilitySummaryTableContent)
);

interface IUtilityBillBreakdownExtended {
  data: IUtilityBillBreakdownAPIResponse;
}

const UtilitySummaryWidget = (props: IDetailComponentProps) => {
  const [t] = useTranslation();
  const d = descriptors.energyManagement;
  const { dataConnector, emaasContext, utilityType, isMonthly } = props;
  const [costCurrency, setCostCurrency] = useState<string>('');

  const onRefresh = () =>
    dataConnector && dataConnector.actions && dataConnector.actions.getAll();
  const { all, loading, error, status } = dataConnector.data;
  const utilityBillBreakdownData = (all as unknown) as IUtilityBillBreakdownExtended;
  const isDataAvailable = utilityBillBreakdownData ? true : false;

  const [isLoading, setIsLoading] = useState<boolean>(loading);
  const [statusCode, setStatusCode] = useState<number | null>(0);
  const [errorInfo, setErrorInfo] = useState(error);
  const [data, setTableData] = useState({} as IUtilityBillBreakdownExtended);
  const [csvFileName, setCsvFileName] = useState('');

  useEffect(() => {
    setIsLoading(loading);
    if ((isDataAvailable || error || status) && !loading) {
      if (isDataAvailable) {
        const fileName = `${getUtilityTitle(
          utilityBillBreakdownData?.data?.utilityType
        )}_${utilityBillBreakdownData?.data?.aggregationsStartDate}_${
          utilityBillBreakdownData?.data?.aggregationsEndDate
        }`;
        setCsvFileName(fileName);

        if (utilityBillBreakdownData?.data?.summaryTableData.data) {
          utilityBillBreakdownData.data.summaryTableData.data = skipIfAllZero(
            processRowData(
              utilityBillBreakdownData?.data?.summaryTableData.data,
              utilityType ? utilityType : 'Electricity'
            )
          );
          utilityBillBreakdownData.data.summaryTableData.dynamicColumns = eliminateNoValueDynamicColumns(
            utilityBillBreakdownData?.data?.summaryTableData.data,
            utilityBillBreakdownData.data.summaryTableData.dynamicColumns
          );
          setTableData(utilityBillBreakdownData);
          setCostCurrency(utilityBillBreakdownData?.data.utilityCostCurrency);
        }
      }
      setErrorInfo(error);
      setStatusCode(status);
    }
  }, [isDataAvailable, loading, status]);

  return (
    <>
      {data?.data?.summaryTableData?.data &&
        data?.data?.summaryTableData?.data !== null && (
          <SummaryTableWithLoader
            loading={isLoading}
            error={errorInfo}
            statusCode={
              data?.data?.summaryTableData?.data &&
              data?.data?.summaryTableData?.data !== null
                ? statusCode
                : 204
            }
            onRefresh={onRefresh}
            rowData={data?.data?.summaryTableData?.data}
            columnDetails={data?.data?.summaryTableData?.dynamicColumns}
            fileName={csvFileName}
            loadingText={t(d.LOADING)}
            title={t(d.CONNECTIONERRORTITLE)}
            noDataMessage={t(d.NODATAAVAILABLE)}
            connectionErrorMessage={t(d.CONNECTIONERROR)}
            costCurrency={costCurrency}
            utilityType={props.utilityType ?? ''}
            isMonthly={isMonthly}
          />
        )}
    </>
  );
};
export default UtilitySummaryWidget;
